import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Table,
  Button,
  Select,
  Space,
  Modal,
  Form,
  DatePicker,
  TimePicker,
  Input,
  InputNumber,
  Radio,
  Spin,
  message,
} from 'antd';
import { LeftOutlined, RightOutlined, PlusOutlined, SettingOutlined } from '@ant-design/icons';
import moment from 'moment';
import LayoutWrapper from '@iso/components/utility/layoutWrapper.js';
import { commonHelper } from '@iso/lib/helpers/commonHelper';
import { shiftService, workerListService } from '@iso/services';
import { SHIFT_STATUSES, SHIFT_STATUS_COLORS, shiftStatusColor } from '@iso/constants/teamSchedule';
import WorkersManager from './WorkersManager';
import PaySettings from './PaySettings';

const { Option } = Select;
const TIME_FMT = 'HH:mm:ss';

const SHIFT_STATUS_MAP = SHIFT_STATUSES.reduce((m, s) => {
  m[s.value] = s.label;
  return m;
}, {});

const WEEKDAY_OPTS = [
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
  { label: 'Sun', value: 7 },
];
const MAX_OCCURRENCES = 366;
const NEVER_CAP = 26; // materialised horizon when "never ends" is chosen

// Expand an Outlook-style recurrence into occurrence dates (YYYY-MM-DD) from the
// commencement date. Capped for safety; "never" materialises NEVER_CAP shifts.
const buildOccurrences = (start, v) => {
  if (!start) return [];
  const repeat = v.repeat || 'none';
  if (repeat === 'none') return [start.format('YYYY-MM-DD')];

  const interval = Math.max(1, parseInt(v.interval, 10) || 1);
  const endMode = v.endMode || 'count';
  const byCount = endMode === 'count' || endMode === 'never';
  const cap = endMode === 'never' ? NEVER_CAP : Math.max(1, parseInt(v.endCount, 10) || 1);
  const endDate = v.endDate ? v.endDate.clone().startOf('day') : null;
  const out = [];
  const countDone = () => byCount && out.length >= cap;
  const pastEnd = (d) => endMode === 'date' && endDate && d.isAfter(endDate, 'day');
  const limit = () => out.slice(0, byCount ? cap : MAX_OCCURRENCES);

  if (repeat === 'daily' || repeat === 'weekdays') {
    let d = start.clone();
    let safety = 0;
    while (out.length < MAX_OCCURRENCES && safety < 5000) {
      safety += 1;
      if (countDone() || pastEnd(d)) break;
      if (repeat === 'weekdays') {
        if (d.isoWeekday() <= 5) out.push(d.format('YYYY-MM-DD'));
        d = d.clone().add(1, 'days');
      } else {
        out.push(d.format('YYYY-MM-DD'));
        d = d.clone().add(interval, 'days');
      }
    }
    return limit();
  }

  if (repeat === 'monthly') {
    let d = start.clone();
    let safety = 0;
    while (out.length < MAX_OCCURRENCES && safety < 5000) {
      safety += 1;
      if (countDone() || pastEnd(d)) break;
      out.push(d.format('YYYY-MM-DD'));
      d = d.clone().add(interval, 'months');
    }
    return limit();
  }

  // weekly
  const days = (v.weekdays && v.weekdays.length ? v.weekdays : [start.isoWeekday()])
    .slice()
    .sort((a, b) => a - b);
  let weekStart = start.clone().startOf('isoWeek');
  let safety = 0;
  while (out.length < MAX_OCCURRENCES && safety < 500) {
    safety += 1;
    for (let i = 0; i < days.length; i += 1) {
      if (countDone()) break;
      const d = weekStart.clone().isoWeekday(days[i]);
      if (d.isBefore(start, 'day') || pastEnd(d)) continue;
      out.push(d.format('YYYY-MM-DD'));
    }
    if (countDone()) break;
    weekStart = weekStart.clone().add(interval, 'weeks');
    if (endMode === 'date' && endDate && weekStart.isAfter(endDate, 'day')) break;
  }
  return limit();
};

// A plain-English summary of the recurrence (Outlook-style).
const describeRecurrence = (start, v) => {
  const repeat = v.repeat || 'none';
  if (repeat === 'none' || !start) return '';
  const interval = Math.max(1, parseInt(v.interval, 10) || 1);
  let base;
  if (repeat === 'daily') base = interval > 1 ? `every ${interval} days` : 'every day';
  else if (repeat === 'weekdays') base = 'every weekday';
  else if (repeat === 'monthly') base = interval > 1 ? `every ${interval} months` : 'every month';
  else {
    const days = (v.weekdays && v.weekdays.length ? v.weekdays : [start.isoWeekday()])
      .slice()
      .sort((a, b) => a - b)
      .map((wd) => moment().isoWeekday(wd).format('ddd'));
    base = `${interval > 1 ? `every ${interval} weeks on ` : 'every '}${days.join(', ')}`;
  }
  const n = buildOccurrences(start, v).length;
  return `Occurs ${base}, starting ${start.format('dddd, D MMMM YYYY')} — ${n} shift${n === 1 ? '' : 's'}.`;
};

// Outlook-style day-of-week toggle (isoweekday values 1–7) for a Form.Item.
const DayToggle = ({ value = [], onChange = () => {} }) => {
  const toggle = (wd) => {
    const next = value.includes(wd) ? value.filter((x) => x !== wd) : [...value, wd];
    onChange(next.sort((a, b) => a - b));
  };
  return (
    <Space size={6}>
      {WEEKDAY_OPTS.map((o) => (
        <Button
          key={o.value}
          type={value.includes(o.value) ? 'primary' : 'default'}
          shape="circle"
          onClick={() => toggle(o.value)}
        >
          {o.label[0]}
        </Button>
      ))}
    </Space>
  );
};

const TeamSchedule = () => {
  const activePropertyId = useSelector((state) => state.property.activePropertyId);
  const activeProperty = commonHelper.getActiveProperty();
  // Pay/OT config is owner/admin only (mirrors the backend gate).
  const activePermission = useSelector((state) => state.permission.activePermission);
  const canManagePay = ['owner', 'admin'].includes(activePermission);
  const [paySettingsOpen, setPaySettingsOpen] = useState(false);

  const [weekStart, setWeekStart] = useState(moment().startOf('isoWeek'));
  const [shifts, setShifts] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locationFilter, setLocationFilter] = useState('all');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [workersModalOpen, setWorkersModalOpen] = useState(false);
  // Recurrence UI state (drives conditional fields in the New Shift modal).
  const [repeatType, setRepeatType] = useState('none');
  const [endMode, setEndMode] = useState('count');
  const [form] = Form.useForm();

  const weekStartStr = weekStart.format('YYYY-MM-DD');
  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => weekStart.clone().add(i, 'days')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weekStartStr]
  );

  const loadShifts = () => {
    if (!activePropertyId) return;
    setLoading(true);
    shiftService
      .getList(activePropertyId, weekStartStr)
      .then((res) => setShifts(res?.shifts || []))
      .catch(() => message.error('Could not load shifts'))
      .finally(() => setLoading(false));
  };

  useEffect(loadShifts, [activePropertyId, weekStartStr]);

  // People to roster come from the property's worker list (staff + contractors),
  // not the raw user list. Reused after the Workers popup changes the list.
  const loadWorkers = () => {
    if (!activePropertyId) return;
    workerListService
      .getList(activePropertyId)
      .then((res) => setWorkers(res?.workers || []))
      .catch(() => {});
  };

  useEffect(loadWorkers, [activePropertyId]);

  // Locations for the filter dropdown (shift blocks are coloured by status now,
  // not location).
  const locations = useMemo(() => {
    const set = new Set();
    shifts.forEach((s) => set.add(s.location || 'Unassigned'));
    return Array.from(set);
  }, [shifts]);

  const visibleShifts =
    locationFilter === 'all'
      ? shifts
      : shifts.filter((s) => (s.location || 'Unassigned') === locationFilter);

  // Rows: every active worker, plus any worker who has a shift but is no longer
  // in the active list (defensive, so their shifts still render).
  const rows = useMemo(() => {
    const map = {};
    workers.forEach((w) => {
      map[w.id] = { worker_id: w.id, name: w.name, type: w.type };
    });
    visibleShifts.forEach((s) => {
      if (!map[s.worker_id]) {
        map[s.worker_id] = { worker_id: s.worker_id, name: s.worker_name || 'Unknown' };
      }
    });
    return Object.values(map).sort((a, b) => String(a.name).localeCompare(b.name));
  }, [workers, visibleShifts]);

  // shiftsByWorker[worker_id][date] = [shift, ...]
  const shiftsByWorker = useMemo(() => {
    const grouped = {};
    visibleShifts.forEach((s) => {
      grouped[s.worker_id] = grouped[s.worker_id] || {};
      grouped[s.worker_id][s.shift_date] = grouped[s.worker_id][s.shift_date] || [];
      grouped[s.worker_id][s.shift_date].push(s);
    });
    return grouped;
  }, [visibleShifts]);

  const openNew = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      shift_date: weekStart.clone(),
      location: activeProperty?.name,
      status: 'scheduled',
      repeat: 'none',
      interval: 1,
      endMode: 'count',
      endCount: 4,
    });
    setRepeatType('none');
    setEndMode('count');
    setModalOpen(true);
  };

  const openEdit = (shift) => {
    setEditing(shift);
    form.setFieldsValue({
      worker_id: shift.worker_id,
      shift_date: moment(shift.shift_date, 'YYYY-MM-DD'),
      start_time: moment(shift.start_time, TIME_FMT),
      end_time: moment(shift.end_time, TIME_FMT),
      location: shift.location,
      status: shift.status,
    });
    setModalOpen(true);
  };

  const handleSubmit = (values) => {
    setSubmitting(true);
    const base = {
      property_id: activePropertyId,
      worker_id: values.worker_id,
      start_time: values.start_time.format(TIME_FMT),
      end_time: values.end_time.format(TIME_FMT),
      location: values.location || null,
      status: values.status || 'scheduled',
    };
    // Editing changes only the one shift; new shifts expand the recurrence into
    // one shift per occurrence via the bulk endpoint.
    const req = editing
      ? shiftService.update(editing.id, { ...base, shift_date: values.shift_date.format('YYYY-MM-DD') })
      : shiftService.createBulk({ ...base, dates: buildOccurrences(values.shift_date, values) });
    req
      .then((res) => {
        if (res && res.code === '0000') {
          const n = res.created;
          message.success(
            editing ? 'Shift updated' : n && n > 1 ? `${n} shifts added` : 'Shift added'
          );
          setModalOpen(false);
          loadShifts();
        } else {
          message.error(res?.message || 'Could not save shift');
        }
      })
      .catch(() => message.error('Could not save shift'))
      .finally(() => setSubmitting(false));
  };

  const handleDelete = () => {
    if (!editing) return;
    setSubmitting(true);
    shiftService
      .destroy(editing.id)
      .then((res) => {
        if (res && res.code === '0000') {
          message.success('Shift removed');
          setModalOpen(false);
          loadShifts();
        } else {
          message.error('Could not remove shift');
        }
      })
      .catch(() => message.error('Could not remove shift'))
      .finally(() => setSubmitting(false));
  };

  const columns = [
    {
      title: 'Worker',
      key: 'worker',
      fixed: 'left',
      width: 180,
      render: (_, row) => <strong>{row.name}</strong>,
    },
    ...weekDates.map((date) => {
      const dateStr = date.format('YYYY-MM-DD');
      return {
        title: (
          <div style={{ textAlign: 'center' }}>
            <div>{date.format('ddd')}</div>
            <div style={{ color: '#8c8c8c', fontWeight: 400 }}>{date.format('D MMM')}</div>
          </div>
        ),
        key: dateStr,
        width: 150,
        render: (_, row) => {
          const dayShifts = shiftsByWorker[row.worker_id]?.[dateStr] || [];
          return dayShifts.map((s) => {
            const c = shiftStatusColor(s.status);
            return (
              <div
                key={s.id}
                onClick={() => openEdit(s)}
                title={SHIFT_STATUS_MAP[s.status] || s.status}
                style={{
                  background: c.bg,
                  color: c.text,
                  border: `1px solid ${c.border}`,
                  borderRadius: 4,
                  padding: '4px 6px',
                  marginBottom: 4,
                  cursor: 'pointer',
                  fontSize: 12,
                }}
              >
                <div style={{ fontWeight: 600 }}>
                  {moment(s.start_time, TIME_FMT).format('H:mm')}–
                  {moment(s.end_time, TIME_FMT).format('H:mm')}
                </div>
                <div>{s.location}</div>
              </div>
            );
          });
        },
      };
    }),
  ];

  return (
    <LayoutWrapper style={{ display: 'block' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <h1 style={{ margin: 0, fontWeight: 700, fontSize: 26 }}>Team Schedule</h1>
        <Space>
          <Button onClick={() => setWorkersModalOpen(true)}>Workers</Button>
          {canManagePay ? (
            <Button
              icon={<SettingOutlined />}
              onClick={() => setPaySettingsOpen(true)}
              title="Pay & overtime settings"
            >
              Pay &amp; OT
            </Button>
          ) : null}
          <Button
            onClick={() =>
              message.info('Shift templates are coming in a later update')
            }
          >
            Templates
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>
            New Shift
          </Button>
        </Space>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Space>
          <Button icon={<LeftOutlined />} onClick={() => setWeekStart(weekStart.clone().subtract(7, 'days'))} />
          <span style={{ fontWeight: 500 }}>
            {weekStart.format('D MMM')} — {weekStart.clone().add(6, 'days').format('D MMM YYYY')}
          </span>
          <Button icon={<RightOutlined />} onClick={() => setWeekStart(weekStart.clone().add(7, 'days'))} />
          <Button onClick={() => setWeekStart(moment().startOf('isoWeek'))}>Today</Button>
        </Space>
        <Select value={locationFilter} style={{ width: 200 }} onChange={setLocationFilter}>
          <Option value="all">All locations</Option>
          {locations.map((loc) => (
            <Option key={loc} value={loc}>
              {loc}
            </Option>
          ))}
        </Select>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 12 }}>
        {SHIFT_STATUSES.map((s) => {
          const c = shiftStatusColor(s.value);
          return (
            <span key={s.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#667085' }}>
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 3,
                  background: c.bg,
                  border: `1px solid ${c.border}`,
                  display: 'inline-block',
                }}
              />
              {s.label}
            </span>
          );
        })}
      </div>

      <Spin spinning={loading}>
        <Table
          rowKey="worker_id"
          columns={columns}
          dataSource={rows}
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'No workers to schedule' }}
        />
      </Spin>

      <Modal
        title={editing ? 'Edit Shift' : 'New Shift'}
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={[
          editing ? (
            <Button key="delete" danger onClick={handleDelete}>
              Delete
            </Button>
          ) : null,
          <Button key="cancel" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="save" type="primary" loading={submitting} onClick={() => form.submit()}>
            Save
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={(changed) => {
            if ('repeat' in changed) {
              setRepeatType(changed.repeat);
              // Default weekly to the commencement weekday.
              if (changed.repeat === 'weekly' && !(form.getFieldValue('weekdays') || []).length) {
                const sd = form.getFieldValue('shift_date');
                if (sd) form.setFieldsValue({ weekdays: [sd.isoWeekday()] });
              }
            }
            if ('endMode' in changed) setEndMode(changed.endMode);
          }}
        >
          <Form.Item
            name="worker_id"
            label="Worker"
            rules={[{ required: true, message: 'Please choose a worker' }]}
          >
            <Select placeholder="Select a worker" showSearch optionFilterProp="children">
              {workers.map((w) => (
                <Option key={w.id} value={w.id}>
                  {w.name}
                  {w.type === 'contractor' ? ' (Contractor)' : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="shift_date"
            label="Date"
            rules={[{ required: true, message: 'Please pick a date' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Space>
            <Form.Item
              name="start_time"
              label="Start"
              rules={[{ required: true, message: 'Required' }]}
            >
              <TimePicker format="HH:mm" minuteStep={15} />
            </Form.Item>
            <Form.Item
              name="end_time"
              label="End"
              rules={[{ required: true, message: 'Required' }]}
            >
              <TimePicker format="HH:mm" minuteStep={15} />
            </Form.Item>
          </Space>
          <Form.Item name="location" label="Location / Station">
            <Input placeholder="e.g. Springfield Station" />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="scheduled">
            <Select>
              {SHIFT_STATUSES.map((s) => {
                const c = shiftStatusColor(s.value);
                return (
                  <Option key={s.value} value={s.value}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 2,
                          background: c.bg,
                          border: `1px solid ${c.border}`,
                          display: 'inline-block',
                        }}
                      />
                      {s.label}
                    </span>
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          {!editing ? (
            <>
              <Form.Item name="repeat" label="Repeat">
                <Select>
                  <Option value="none">Does not repeat</Option>
                  <Option value="daily">Repeat daily</Option>
                  <Option value="weekdays">Every weekday (Mon–Fri)</Option>
                  <Option value="weekly">Repeat weekly</Option>
                  <Option value="monthly">Repeat monthly</Option>
                </Select>
              </Form.Item>

              {repeatType === 'daily' || repeatType === 'weekly' || repeatType === 'monthly' ? (
                <Form.Item
                  name="interval"
                  label={`Repeat every (${repeatType === 'daily' ? 'days' : repeatType === 'weekly' ? 'weeks' : 'months'})`}
                >
                  <InputNumber min={1} max={365} style={{ width: 160 }} />
                </Form.Item>
              ) : null}

              {repeatType === 'weekly' ? (
                <Form.Item name="weekdays" label="On days">
                  <DayToggle />
                </Form.Item>
              ) : null}

              {repeatType !== 'none' ? (
                <>
                  <Form.Item name="endMode" label="Ends">
                    <Radio.Group>
                      <Radio value="never">Never</Radio>
                      <Radio value="count">After a number of occurrences</Radio>
                      <Radio value="date">On a date</Radio>
                    </Radio.Group>
                  </Form.Item>
                  {endMode === 'date' ? (
                    <Form.Item
                      name="endDate"
                      label="End date"
                      rules={[{ required: true, message: 'Please pick an end date' }]}
                    >
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                  ) : endMode === 'count' ? (
                    <Form.Item name="endCount" label="Number of occurrences">
                      <InputNumber min={1} max={366} style={{ width: 160 }} />
                    </Form.Item>
                  ) : (
                    <div style={{ color: '#98a2b3', fontSize: 12, marginTop: -8, marginBottom: 12 }}>
                      "Never" creates the next {NEVER_CAP} shifts (extend later as needed).
                    </div>
                  )}

                  <Form.Item noStyle shouldUpdate>
                    {() => {
                      const text = describeRecurrence(
                        form.getFieldValue('shift_date'),
                        form.getFieldsValue()
                      );
                      return text ? (
                        <div style={{ background: '#f9fafb', border: '1px solid #eaecf0', borderRadius: 8, padding: '10px 12px', color: '#475467', fontSize: 13 }}>
                          {text}
                        </div>
                      ) : null;
                    }}
                  </Form.Item>
                </>
              ) : null}
            </>
          ) : null}
        </Form>
      </Modal>

      <Modal
        title="Workers"
        visible={workersModalOpen}
        onCancel={() => {
          setWorkersModalOpen(false);
          // Pick up any adds/removes made in the popup.
          loadWorkers();
        }}
        footer={null}
        width="95%"
        style={{ top: 20, maxWidth: 1120 }}
        destroyOnClose
      >
        <WorkersManager onChanged={loadWorkers} />
      </Modal>

      <Modal
        title="Pay & Overtime settings"
        visible={paySettingsOpen}
        onCancel={() => setPaySettingsOpen(false)}
        footer={null}
        width="95%"
        style={{ top: 20, maxWidth: 1200 }}
        destroyOnClose
      >
        <PaySettings />
      </Modal>
    </LayoutWrapper>
  );
};

export default TeamSchedule;
